import type { GenericSchema, InferOutput } from 'valibot'
import type { BaseBody } from './body'
import type { BaseCommentary } from './commentary'
import type { BaseLaw } from './law'
import type { LawCommentaryNodeType } from './law-node-type'
import { array, intersect, lazy, nullish, object, pipe, string, transform, union } from 'valibot'
import { baseBodySchema } from './body'
import { commentarySchema } from './commentary'
import { baseLawSchema } from './law'
import { lawNodeTypeSchema } from './law-node-type'

export type { LawCommentaryNodeType } from './law-node-type'

/* Due to a TypeScript limitation, the input and output types of recursive schemas cannot be inferred automatically.
Therefore, you must explicitly specify these types using GenericSchema.
*/
type LawCommentary = BaseBody & {
  docs: Doc[]
}

type Doc = BaseLaw & BaseCommentary & {
  nodeType?: LawCommentaryNodeType | null
  children?: Doc[] | null
  comments?: LawCommentary | null
}

export const lawCommentarySchema = intersect([
  baseBodySchema,
  object({
    docs: array(lazy(() => commentarySchema)),
  }),
])

export const lawAndCommentarySchema: GenericSchema<Doc> = intersect([
  baseLawSchema,
  commentarySchema,
  object({
    nodeType: lawNodeTypeSchema,
    // Ensure children are valid Doc objects (this fixes issues with Vergemålsloven - vgml. (lov-1927-04-22-3) and other laws where children's array has empty strings)
    children: nullish(pipe(
      array(union([
        lazy(() => lawAndCommentarySchema),
        string(),
      ])),
      transform((val: (Doc | string)[]) => {
        const filtered = val.filter((child): child is Doc => typeof child === 'object' && child !== null)
        return Array.isArray(filtered) && filtered.length > 0 ? filtered : undefined
      }),
    )),
    comments: nullish(lawCommentarySchema),
  }),
]) as unknown as GenericSchema<Doc> // TypeScript need helps to infer the type of recursive schemas when using transform

export type LawAndCommentary = InferOutput<typeof lawAndCommentarySchema>
